import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useForm } from './FormManager';
import './Footer.css';

const Footer = () => {
  const { openGeneralForm } = useForm();
  const { t } = useTranslation('common');
  const [isAccordionOpen, setAccordionOpen] = React.useState(false);
  const footerRef = useRef(null);
  const contentRef = useRef(null);
  const bottomRef = useRef(null);

  // Animation on scroll
  useEffect(() => {
    const footer = footerRef.current;
    const content = contentRef.current;
    const bottom = bottomRef.current;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          footer.classList.add('visible');
          setTimeout(() => content.classList.add('visible'), 300);
          setTimeout(() => bottom.classList.add('visible'), 600);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });

    if (footer) {
      observer.observe(footer);
    }

    return () => {
      if (footer) {
        observer.unobserve(footer);
      }
    };
  }, []);

  const currentYear = new Date().getFullYear();

  const serviceRegionLinks = [
    { name: 'Webdesign Frankfurt', path: '/services#webdesign-frankfurt' },
    { name: 'Webdesign Darmstadt', path: '/services#webdesign-darmstadt' },
    { name: 'Webdesign Aschaffenburg', path: '/services#webdesign-aschaffenburg' },
    { name: 'Webdesign Stuttgart', path: '/services#webdesign-stuttgart' },
    { name: 'Webdesign Ulm', path: '/services#webdesign-ulm' },
    { name: 'SEO Agentur Frankfurt', path: '/services#seo-frankfurt' },
    { name: 'SEO Agentur Darmstadt', path: '/services#seo-darmstadt' },
    { name: 'SEO Agentur Aschaffenburg', path: '/services#seo-aschaffenburg' },
    { name: 'SEO Agentur Stuttgart', path: '/services#seo-stuttgart' },
    { name: 'SEO Agentur Ulm', path: '/services#seo-ulm' },
    { name: 'Google Ads Frankfurt', path: '/services#ads-frankfurt' },
    { name: 'Google Ads Darmstadt', path: '/services#ads-darmstadt' },
    { name: 'Google Ads Aschaffenburg', path: '/services#ads-aschaffenburg' },
    { name: 'Google Ads Stuttgart', path: '/services#ads-stuttgart' },
    { name: 'Google Ads Ulm', path: '/services#ads-ulm' },
    { name: 'Buchungssystem Frankfurt', path: '/services#booking-frankfurt' },
    { name: 'Buchungssystem Darmstadt', path: '/services#booking-darmstadt' },
    { name: 'Buchungssystem Aschaffenburg', path: '/services#booking-aschaffenburg' },
    { name: 'Buchungssystem Stuttgart', path: '/services#booking-stuttgart' },
    { name: 'Buchungssystem Ulm', path: '/services#booking-ulm' },
    { name: 'Reputation Management Frankfurt', path: '/services#reputation-frankfurt' },
    { name: 'Reputation Management Darmstadt', path: '/services#reputation-darmstadt' },
    { name: 'Reputation Management Aschaffenburg', path: '/services#reputation-aschaffenburg' },
    { name: 'Reputation Management Stuttgart', path: '/services#reputation-stuttgart' },
    { name: 'Reputation Management Ulm', path: '/services#reputation-ulm' },
    { name: 'E-Mail Marketing Frankfurt', path: '/services#email-frankfurt' },
    { name: 'E-Mail Marketing Darmstadt', path: '/services#email-darmstadt' },
    { name: 'E-Mail Marketing Aschaffenburg', path: '/services#email-aschaffenburg' },
    { name: 'E-Mail Marketing Stuttgart', path: '/services#email-stuttgart' },
    { name: 'E-Mail Marketing Ulm', path: '/services#email-ulm' },
  ];

  return (
    <footer className="footer" ref={footerRef}>
      <div className="container">
        <div className="footer-content" ref={contentRef}>
          <div className="footer-brand">
            <div className="footer-logo">ENZLO</div>
            <p className="footer-tagline">{t('footerTagline', 'Digitale Exzellenz für Ihr Unternehmen.')}</p>
          </div>

          <div className="footer-links">
            <div className="footer-links-column">
              <h3 className="footer-links-title">{t('footerLeistungenTitle', 'Leistungen')}</h3>
              <ul className="footer-links-list">
                <li><Link to="/website-checkliste-fuer-salons-und-handwerker">Webdesign</Link></li>
                <li><Link to="/google-maps-seo-optimieren">Local SEO</Link></li>
                <li><Link to="/blog/google-meta-ads-fuer-salons-und-handwerker">Google Ads</Link></li>
                <li><Link to="/blog/online-buchungssystem-masterclass">Buchungssysteme</Link></li>
                <li><Link to="/blog/reputationsmanagement-masterclass">Reputation</Link></li>
                <li><Link to="/blog/email-marketing-masterclass">E-Mail Marketing</Link></li>
              </ul>
            </div>

            <div className="footer-links-column">
              <h3 className="footer-links-title">Agentur</h3>
              <ul className="footer-links-list">
                <li><Link to="/seo-agentur">SEO Agentur</Link></li>
                <li><Link to="/local-seo-agentur">Local-SEO-Agentur</Link></li>
                <li><Link to="/google-ads-agentur">Ads Agentur</Link></li>
                <li><Link to="/performance-marketing-agentur">Performance Marketing Agentur</Link></li>
                <li><Link to="/online-marketing-agentur">Online Marketing Agentur</Link></li>
                <li><Link to="/digitalagentur">Digitalagentur</Link></li>
                <li><Link to="/werbeagentur">Werbeagentur</Link></li>
                <li><Link to="/full-service-agentur">Full Service Agentur</Link></li>
                <li><Link to="/ki-agentur">KI Agentur</Link></li>
              </ul>
            </div>

            <div className="footer-links-column">
              <h3 className="footer-links-title">Unternehmen</h3>
              <ul className="footer-links-list">
                <li><Link to="/about">Über Uns</Link></li>
                <li><Link to="/pricing">Preise</Link></li>
                <li><a href="#" onClick={(e) => { e.preventDefault(); openGeneralForm(); }} style={{ cursor: 'pointer' }}>Mehr Kunden gewinnen</a></li>
                <li><Link to="/blog">Blog</Link></li>
              </ul>
            </div>

            <div className="footer-links-column">
              <h3 className="footer-links-title">Rechtliches</h3>
              <ul className="footer-links-list">
                <li><Link to="/impressum">Impressum</Link></li>
                <li><Link to="/datenschutz">Datenschutz</Link></li>
              </ul>
            </div>

            <div className="footer-links-column footer-contact-column">
              <h3 className="footer-links-title">Kontakt</h3>
              <div className="footer-contact">
                <p className="footer-contact-item">
                  <a href="mailto:hi@enzlo.com" className="contact-value">hi@enzlo.com</a>
                </p>
                <p className="footer-contact-item">
                  <a href="tel:+4915751619973" className="contact-value">+49 157 516 19973</a>
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="footer-accordion">
          <button className="accordion-toggle" onClick={() => setAccordionOpen(!isAccordionOpen)}>
            Spezifische Leistungen & Regionen
            <span className={`accordion-icon ${isAccordionOpen ? 'open' : ''}`}>+</span>
          </button>
          <div className={`accordion-content ${isAccordionOpen ? 'open' : ''}`}>
            <ul className="footer-links-list-grid">
              {serviceRegionLinks.map(link => (
                <li key={link.name}><Link to={link.path}>{link.name}</Link></li>
              ))}
            </ul>
          </div>
        </div>

        <div className="footer-bottom" ref={bottomRef}>
          <div className="footer-copyright">
            &copy; {currentYear} Enzlo. {t('footerCopyright', 'Alle Rechte vorbehalten.')}
          </div>
          <div className="footer-socials">
            <a href="https://www.youtube.com/@Totalemailautoresponder" target="_blank" rel="noopener noreferrer" style={{marginRight: '1.2rem'}}>YouTube</a>
            <a href="https://www.facebook.com/enzloinc/" target="_blank" rel="noopener noreferrer">Facebook</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
